import React, { useEffect, useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import styled, { css } from 'styled-components';
import { colors } from '../../../config/styles';
import { useDndEntityInternalContext } from '../';
import { IEntityDropzoneDataProperties } from '../validation';
import { DND_BASE_LAYER_Z_INDEX } from '../constants';
import { theme } from '../../../styles/theme';
import { floatingComponentStyles } from '../../../styles/shareableStyles';

export interface IProps {
  id: string;
  accepts: string[];
  data?: IEntityDropzoneDataProperties;
  shouldFitContent?: boolean;
  shouldFillContent?: boolean;
  hasError?: boolean;
  customMessage?: string;
  isDisabled?: boolean;
  isEmbedded?: boolean;
}

const Container = styled.div<{
  $isOver: boolean;
  $isAcceptable: boolean;
  $shouldFitContent?: boolean;
  shouldFillContent?: boolean;
  $hasError?: boolean;
  $isEmbedded?: boolean;
  $shouldFillContent?: boolean;
}>`
  position: relative;

  ${({ $shouldFitContent }) =>
    $shouldFitContent &&
    `
    width: fit-content;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 2px;
  `};

  ${({ $isAcceptable }) =>
    $isAcceptable &&
    css`
      z-index: ${DND_BASE_LAYER_Z_INDEX};
      outline: 2px solid ${theme.colors.secondary600};
      border-radius: 4px;
      background-color: ${theme.colors.contentHighlight};
    `};

  ${({ $isOver, $hasError }) =>
    $isOver &&
    `
    outline: 2px solid ${$hasError ? colors.mainError : colors.normalGreen};
    border-radius: 4px;
  `};

  ${({ $isEmbedded, $isOver }) =>
    $isEmbedded &&
    `
    border: none;
    border-top: 2px solid ${$isOver ? colors.normalGreen : theme.colors.secondary600};
    border-radius: 0px;
  `};

  ${({ $shouldFillContent }) =>
    $shouldFillContent &&
    `
    width: 100%;
    border-radius: 0px;
  `};
`;

const ActionText = styled.div`
  z-index: ${DND_BASE_LAYER_Z_INDEX + 2};
  position: absolute;
  top: -32px;
  right: 5px;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${theme.colors.secondary900};
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  ${floatingComponentStyles};
`;

const DropzoneOverlay = styled.div<{ $hasError: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: ${DND_BASE_LAYER_Z_INDEX + 4};
  opacity: 0.5;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  top: 0;
  background-color: ${({ $hasError }) =>
    $hasError ? 'none' : colors.hoverGreen};
`;

const DndDroppableZone = ({
  id,
  accepts,
  children,
  data,
  shouldFitContent,
  shouldFillContent,
  hasError,
  customMessage,
  isDisabled = false,
  isEmbedded = false,
}: React.PropsWithChildren<IProps>) => {
  const { isOver, setNodeRef } = useDroppable({
    id,
    data: {
      accepts,
      data,
    },
  });

  const dndContext = useDndEntityInternalContext();

  const { activeItem, setDropzoneErrors } = dndContext;

  const isAcceptable = useMemo(() => {
    if (activeItem) {
      const type = activeItem.data.current?.type;
      if (type) {
        return accepts.includes(type);
      }
    }
    return false;
  }, [activeItem, accepts]);

  // Set error state in DndEntity context if this dropzone has an error
  useEffect(() => {
    setDropzoneErrors((prev) => ({ ...prev, [id]: !!hasError }));
  }, [id, hasError, setDropzoneErrors]);

  if (isDisabled) {
    return <>{children}</>;
  }

  return (
    <Container
      ref={setNodeRef}
      $isAcceptable={isAcceptable}
      $isOver={isAcceptable && isOver}
      $shouldFitContent={shouldFitContent}
      $shouldFillContent={shouldFillContent}
      $hasError={hasError}
      $isEmbedded={isEmbedded}
    >
      {children}
      {isAcceptable && isOver && <DropzoneOverlay $hasError={!!hasError} />}
      {isAcceptable && isOver && customMessage && (
        <ActionText>{customMessage}</ActionText>
      )}
    </Container>
  );
};
export default DndDroppableZone;
